import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, Alert,
} from '@mui/material';
import FlexService from '../../services/FlexService';

// The photos on one record -- a damaged box on a receipt, a received LPN, an
// item's picture -- the same photos the RF gun takes (image_list / image_show /
// image_add / image_delete; the WMS keeps them). Opened by a workspace row action
// of type PHOTOS: { "type": "PHOTOS", "photos": { "entity": "Receipt", "idField": "receiptId" } }.

const MAX_SIDE = 1600;

// A picked file (often several MB) -> a JPEG data URL, longest side MAX_SIDE, as on the gun.
function shrink(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not a picture.')); };
    img.src = url;
  });
}

const run = async (scriptName, payload) => {
  const resp = await FlexService.executeScript({ scriptName, context: { _payload: payload } });
  if (resp?.success === false) throw new Error(resp?.context?.error_message || resp?.errorMessage || 'The warehouse system did not answer.');
  return resp?.context?.result;
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

export default function PhotosDialog({ target, colors = {}, onClose }) {
  const open = !!target;
  const [photos, setPhotos] = useState(null);
  const [images, setImages] = useState({}); // fileId -> data URL
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    if (!target) return;
    setError('');
    try {
      const list = await run('image_list', { entity: target.entity, entityId: target.entityId });
      setPhotos(Array.isArray(list) ? list : []);
      (Array.isArray(list) ? list : []).filter((p) => p.hasData).forEach(async (p) => {
        try {
          const one = await run('image_show', { entity: target.entity, fileId: p.fileId });
          const data = one?.imageData || one?.data?.imageData;
          const type = one?.contentType || one?.data?.contentType || 'image/jpeg';
          if (data) setImages((m) => ({ ...m, [p.fileId]: `data:${type};base64,${data}` }));
        } catch { /* the tile says it could not be shown */ }
      });
    } catch (e) {
      setPhotos([]);
      setError(e.message);
    }
  }, [target]);

  useEffect(() => {
    setPhotos(null);
    setImages({});
    setBig(null);
    load();
  }, [load]);

  const add = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const dataUrl = await shrink(file);
      await run('image_add', { entity: target.entity, entityId: target.entityId, imageData: dataUrl, contentType: 'image/jpeg' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove this photo from ${target.label}? It can't be brought back.`)) return;
    setBusy(true);
    try {
      await run('image_delete', { entity: target.entity, fileId: p.fileId });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 0, bgcolor: colors.surface || colors.background } }}>
      <DialogTitle sx={{ color: colors.textPrimary }}>Photos of {target?.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          Photos taken on the RF gun or added here. Click a photo to see it large.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
        {photos === null ? <CircularProgress size={24} /> : null}
        {photos && photos.length === 0 ? (
          <Typography sx={{ color: colors.textSecondary }}>No photos yet.</Typography>
        ) : null}
        {big ? (
          <Box>
            <img src={images[big.fileId]} alt={`Photo ${big.fileId} of ${target?.label}`} style={{ width: '100%', maxHeight: '65vh', objectFit: 'contain' }} />
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
              {when(big.createdAt)}{big.takenBy || big.createdBy ? ` · ${big.takenBy || big.createdBy}` : ''}
            </Typography>
            <Button sx={{ mt: 1 }} onClick={() => setBig(null)}>Back to all photos</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            {(photos || []).map((p) => (
              <Box key={p.fileId} sx={{ border: `1px solid ${colors.border || '#ccc'}`, p: 1 }}>
                {p.hasData ? (
                  images[p.fileId] ? (
                    <img
                      src={images[p.fileId]}
                      alt={`Photo ${p.fileId} of ${target?.label}`}
                      style={{ width: '100%', height: 140, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setBig(p)}
                    />
                  ) : <Box sx={{ height: 140, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Box>
                ) : (
                  <Typography variant="body2" sx={{ height: 140, color: colors.textSecondary }}>
                    Taken with the old iOS app; kept in its own storage, not here.
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary, mt: 0.5 }}>
                  {when(p.createdAt)}{p.takenBy || p.createdBy ? ` · ${p.takenBy || p.createdBy}` : ''}
                </Typography>
                <Button size="small" color="error" disabled={busy} onClick={() => remove(p)}>Remove</Button>
              </Box>
            ))}
          </Box>
        )}
        <input ref={fileInput} type="file" accept="image/*" aria-label="Photo to add" style={{ display: 'none' }} onChange={add} />
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => fileInput.current?.click()}>{busy ? 'Saving…' : 'Add a photo'}</Button>
        <Button variant="contained" disabled={busy} onClick={onClose} sx={{ borderRadius: 0 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
